<script setup lang="ts">
  import { computed, reactive, ref, watch, watchEffect } from 'vue'
  import { Button, message } from 'ant-design-vue'
  import { Trash2 } from 'lucide-vue-next'
  import {
    PlusOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    HolderOutlined,
    CompressOutlined,
    ExpandOutlined
  } from '@ant-design/icons-vue'
  import { PlotSchema } from '../models/PlotSchema'
  import { PlotField } from '../models/PlotField'
  import { PlotFieldType } from '../models/PlotFieldType'
  import { Plot } from '../models/Plot'
  import { PlottingService } from '../services/PlottingService'
  import { defaultMessageDurationSeconds } from '@/utils'
  import PlotConfiguration from './PlotConfiguration.vue'
  import { PlotDataSourceType } from '../models/PlotDataSourceType'

  interface IArrayPlotBuilderProps {
    plotSchemas: Array<PlotSchema>
    availableColumns: Array<{ value: string; label: string }>
    availableParams: Array<{ value: string; label: string }>
    availableDataSources: Array<{
      id: string
      value: string
      label: string
      type: PlotDataSourceType
    }>
    plotField: PlotField
    modelValue: []
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
  }

  interface IArrayPlotBuilderEmits {
    // We have setup reactivity to auto-update based on rules array model changes
    (e: 'update:modelValue', value: Array<any>): void
    // Here we are just accounting for type change notification, note that plots
    // can represent a plot or a plot object
    (e: 'child-plot-type-changed', type: string, plot: Plot, configuration: {}): void
    (e: 'data-source-selected', value: PlotDataSourceType): void
  }

  const props = defineProps<IArrayPlotBuilderProps>()
  const emit = defineEmits<IArrayPlotBuilderEmits>()

  const arrayPlotField = reactive<PlotField>(props.plotField)

  if (arrayPlotField.field_type !== PlotFieldType.Array)
    throw new Error('ArrayPlotBuilder requires an Array type PlotField!')
  if (arrayPlotField.item_schema == null)
    throw new Error('Array type PlotFields require an item_schema!')

  // collection of plots and plot configurations that make up sub-items in ploy JSON
  const plots = ref<Array<Plot>>([])
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }
  // Drag/drop state
  const draggedIndex = ref<number | null>(null)
  const dragOverIndex = ref<number | null>(-1)
  const instanceId = `arrayPlot-${Math.random().toString(36).slice(2)}`

  const handleDataSourceSelected = (value: PlotDataSourceType) => {
    emit('data-source-selected', value)
  }

  watch(
    props.modelValue,
    (newVal) => {
      plots.value = []
      if (Array.isArray(newVal)) {
        newVal.forEach((value: any) => {
          const plot = PlottingService.createPlotFromPlotField(arrayPlotField.item_schema!)
          plot.configuration =
            value.configuration != null ? PlottingService.getFlattenedPlot(value, null) : value
          plot.configuration.type = plot.type
          plots.value.push(plot)
        })
      }
    },
    { immediate: true, deep: true }
  )

  watch(
    plots,
    (newValue) => {
      emit('update:modelValue', newValue)
    },
    { deep: true }
  )

  // Initialize configuration with default values
  const initializeConfiguration = (plot: Plot, fields: Array<PlotField>) => {
    if (plot == null) throw new Error('plot is required')
    if (arrayPlotField.item_schema?.field_type !== PlotFieldType.Object) return
    const config = PlottingService.initializePlotFieldConfiguration(fields)
    plot.configuration = config
  }

  const isAddDisabled = computed(() => {
    const maxItems = (arrayPlotField.field_type_args as any)?.array_max_items
    if (maxItems === undefined || maxItems === null) return false
    if (typeof maxItems !== 'number' || isNaN(maxItems) || maxItems < 0) return false
    return plots.value.length >= maxItems
  })

  const addPlot = () => {
    // Create new plot object from item_schema
    const newPlot = PlottingService.createPlotFromPlotField(arrayPlotField.item_schema!)
    initializeConfiguration(newPlot, arrayPlotField.item_schema?.fields || [])
    plots.value.push(newPlot)
    message.success(`Added rule: ${arrayPlotField.display_name}`, defaultMessageDurationSeconds)
    return newPlot
  }

  const removePlot = (index: number) => {
    plots.value.splice(index, 1)
  }

  const moveItem = (from: number, to: number) => {
    if (from === to) return
    const item = plots.value.splice(from, 1)[0]
    plots.value.splice(to, 0, item)
  }

  const moveUp = (index: number) => {
    if (index > 0) {
      moveItem(index, index - 1)
    }
  }

  const moveDown = (index: number) => {
    if (index < plots.value.length - 1) {
      moveItem(index, index + 1)
    }
  }

  const handleDragLeave = () => {
    dragOverIndex.value = null
  }

  const handleDragStart = (event: DragEvent, index: number) => {
    event.stopPropagation()
    draggedIndex.value = index
    const id = instanceId
    // store origin in dataTransfer and window fallback
    try {
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', String(index))
        event.dataTransfer.setData('application/x-array-builder-id', id)
      }
    } catch {}
    ;(window as any).__arrayBuilderDragSourceId = id
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    event.stopPropagation()
    let sourceId: string | null = null
    try {
      sourceId = event.dataTransfer?.getData('application/x-array-builder-id') || null
    } catch {}
    sourceId = sourceId || (window as any).__arrayBuilderDragSourceId || null
    if (sourceId !== instanceId) {
      dragOverIndex.value = -1
      return
    }
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    dragOverIndex.value = index
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()
    event.stopPropagation()
    let sourceId: string | null = null
    try {
      sourceId = event.dataTransfer?.getData('application/x-array-builder-id') || null
    } catch {}
    sourceId = sourceId || (window as any).__arrayBuilderDragSourceId || null
    if (sourceId !== instanceId) return

    if (draggedIndex.value == null) return
    const from = draggedIndex.value
    const to = targetIndex
    if (from === to) {
      draggedIndex.value = null
      dragOverIndex.value = -1
      ;(window as any).__arrayBuilderDragSourceId = null
      return
    }
    moveItem(from, to)
    draggedIndex.value = null
    dragOverIndex.value = -1
    ;(window as any).__arrayBuilderDragSourceId = null
  }

  const handleDragEnd = () => {
    draggedIndex.value = null
    dragOverIndex.value = -1
    ;(window as any).__arrayBuilderDragSourceId = null
  }

  const handleConfigurationUpdate = (
    plot: Plot,
    config: Record<string, any>,
    plotTypeChange?: boolean
  ) => {
    const plotIndex = plots.value.findIndex((r) => r.id === plot.id)
    if (plotIndex > -1) {
      plots.value[plotIndex].configuration = { ...config }
    }
    if (plotTypeChange === true) {
      emit('child-plot-type-changed', plot.type, plot, { ...config })
    }
  }
</script>

<template>
  <div
    v-if="arrayPlotField.item_schema?.field_type === PlotFieldType.Object"
    class="plot-array-object-builder-container"
  >
    <div class="mt-[-40px] flex items-center gap-2 self-end">
      <a-tooltip placement="top">
        <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
        <Button
          style="padding: 3px; border: none"
          type="text"
          size="large"
          class="flex items-center justify-center"
          @click="toggleGlobalCollapse"
          :disabled="plots.length === 0"
          :aria-disabled="plots.length === 0"
          aria-label="Toggle Collapse All"
        >
          <CompressOutlined
            v-if="isCollapsed"
            class="text-base"
            style="line-height: 1; margin: 0"
          />
          <ExpandOutlined v-else class="text-base" style="line-height: 1; margin: 0" />
        </Button>
      </a-tooltip>

      <Button
        type="primary"
        shape="circle"
        size="large"
        class="flex items-center justify-center"
        @click="addPlot"
        :title="`Add ${arrayPlotField.item_schema!.display_name}`"
        :disabled="isAddDisabled"
        :aria-disabled="isAddDisabled"
      >
        <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
      </Button>
    </div>

    <div class="mt-5">
      <div
        class="plot-mapping-row"
        v-for="(plot, index) in plots"
        :key="plot.id"
        :class="{ 'drop-zone-active': dragOverIndex === index }"
        @dragover.prevent="handleDragOver($event, index)"
        @dragleave="handleDragLeave"
        @drop.prevent="handleDrop($event, index)"
      >
        <div
          class="drag-handle"
          draggable="true"
          @dragstart="handleDragStart($event, index)"
          @dragend="handleDragEnd"
          tabindex="0"
          role="button"
          :aria-grabbed="draggedIndex === index"
          aria-label="Drag to reorder"
        >
          <HolderOutlined class="text-base" style="line-height: 1; margin: 0" />
        </div>

        <PlotConfiguration
          :configuration-level="props.configurationLevel + 1"
          :plot="plot"
          :available-columns="availableColumns"
          :available-params="availableParams"
          :available-data-sources="availableDataSources"
          @configuration-updated="handleConfigurationUpdate"
          @data-source-selected="handleDataSourceSelected"
          :plot-schemas="plotSchemas"
          :is-condensed-layout="props.isCondensedLayout"
          :global-collapsed="globalCollapsed"
        />

        <div class="action-buttons ml-2 flex flex-col items-center">
          <a-tooltip placement="left">
            <template #title>Move Up</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn"
              @click="moveUp(index)"
              :disabled="index === 0"
              :aria-disabled="index === 0"
            >
              <ArrowUpOutlined class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>

          <a-tooltip placement="left">
            <template #title>{{ `Remove ${arrayPlotField.item_schema!.display_name}` }}</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn ant-btn-dangerous my-1"
              @click="removePlot(index)"
            >
              <Trash2 class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>

          <a-tooltip placement="left">
            <template #title>Move Down</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn"
              @click="moveDown(index)"
              :disabled="index === plots.length - 1"
              :aria-disabled="index === plots.length - 1"
            >
              <ArrowDownOutlined class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .plot-array-object-builder-container {
    display: flex;
    flex-direction: column;
  }

  .plot-mapping-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 15px;
  }

  .drag-handle {
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: transparent;
    color: var(--muted-foreground, #6b7280);
  }

  .plot-mapping-row.drop-zone-active {
    outline: 2px dashed rgba(24, 144, 255, 0.35);
    background-color: rgba(24, 144, 255, 0.03);
  }

  .action-buttons .ant-btn {
    padding: 3px;
  }

  .compact-action-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .compact-icon,
  .compact-icon svg {
    width: 14px !important;
    height: 14px !important;
    line-height: 1 !important;
  }

  select {
    padding: 4px;
  }
</style>
